import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { brandService } from '../../../services/brandService';
import { socialAccountService } from '../../../services/socialAccountService';
import Button from '../../../components/ui/Button';
import { ArrowLeft, Plus, Loader2, Globe, ExternalLink } from 'lucide-react';
import SocialAccountCard from './SocialAccountCard';
import ConnectSocialModal from './ConnectSocialModal';

export default function BrandDetails() {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);

  useEffect(() => {
    loadBrand();
  }, [brandId]);

  const loadBrand = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Validate brandId before making API call
      if (!brandId || brandId === ':brandId' || brandId === 'undefined') {
        setError('Invalid brand ID. Please select a brand from the brand management page.');
        setLoading(false);
        return;
      }
      
      const data = await brandService?.getById(brandId);
      setBrand(data);
    } catch (err) {
      setError(err?.message || 'Failed to load brand details');
      console.error('Error loading brand:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectAccount = async (accountData) => {
    try {
      await socialAccountService?.connect(brandId, accountData);
      await loadBrand();
      setIsConnectModalOpen(false);
    } catch (err) {
      throw new Error(err.message);
    }
  };

  const handleDisconnectAccount = async (accountId) => {
    if (!window.confirm('Are you sure you want to disconnect this social account?')) {
      return;
    }

    try {
      await socialAccountService?.disconnect(accountId);
      await loadBrand();
    } catch (err) {
      setError(err?.message);
      console.error('Error disconnecting account:', err);
    }
  };

  const handleSyncAccount = async (accountId) => {
    try {
      await socialAccountService?.syncFollowers(accountId);
      await loadBrand();
    } catch (err) {
      setError(err?.message);
      console.error('Error syncing account:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!brand && !loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Brand not found'}</p>
          <Button onClick={() => navigate('/brand-management')} className="mt-4">
            Back to Brands
          </Button>
        </div>
      </div>
    );
  }

  const connectedAccounts = brand?.socialAccounts?.filter(acc => acc?.isConnected) || [];
  const totalFollowers = connectedAccounts?.reduce((sum, acc) => sum + acc?.followerCount, 0);

  return (
    <div className="container mx-auto px-4 py-8">
      <Button
        onClick={() => navigate('/brand-management')}
        variant="ghost"
        className="mb-6 flex items-center gap-2"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Brands
      </Button>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center">
              <Globe className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{brand?.name}</h1>
              {brand?.industry && (
                <span className="text-gray-600">{brand?.industry}</span>
              )}
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${brand?.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
            {brand?.isActive ? 'Active' : 'Inactive'}
          </div>
        </div>

        {brand?.description && (
          <p className="text-gray-600 mb-4">{brand?.description}</p>
        )}

        {brand?.website && (
          <a
            href={brand?.website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-700 flex items-center gap-2 mb-4"
          >
            <ExternalLink className="w-4 h-4" />
            {brand?.website}
          </a>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600">Connected Platforms</p>
            <p className="text-2xl font-bold text-gray-900">{connectedAccounts?.length}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600">Total Followers</p>
            <p className="text-2xl font-bold text-gray-900">{totalFollowers?.toLocaleString()}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600">Last Updated</p>
            <p className="text-sm font-medium text-gray-900">
              {new Date(brand.updatedAt)?.toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Social Media Profiles</h2>
          <Button onClick={() => setIsConnectModalOpen(true)} className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Connect Platform
          </Button>
        </div>

        {brand?.socialAccounts && brand?.socialAccounts?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {brand?.socialAccounts?.map((account) => (
              <SocialAccountCard
                key={account?.id}
                account={account}
                onDisconnect={handleDisconnectAccount}
                onSync={handleSyncAccount}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-600 mb-4">No social accounts connected yet</p>
            <Button onClick={() => setIsConnectModalOpen(true)} className="flex items-center gap-2 mx-auto">
              <Plus className="w-5 h-5" />
              Connect Your First Platform
            </Button>
          </div>
        )}
      </div>
      {isConnectModalOpen && (
        <ConnectSocialModal
          isOpen={isConnectModalOpen}
          brandId={brandId}
          onClose={() => setIsConnectModalOpen(false)}
          onConnect={handleConnectAccount}
          existingPlatforms={brand?.socialAccounts?.map(acc => acc?.platform) || []}
        />
      )}
    </div>
  );
}